import { baseStore, getConfigValue, Job } from '@recruiting-solutions/rs-shared';
import { JobService } from '@recruiting-solutions/search-ui-shared';
import { BehaviorSubject } from 'rxjs';
import { Application, Profile } from '../models';
import { ApplyService } from './apply.service';
import { AuthService } from './auth.service';
import { FormPageService } from './form-page.service';

export interface FullProfile {
    signinNeeded?: boolean;
    profile?: Profile;
    applications?: { application: Application; job?: Job }[];
    reload?: () => Promise<void>;
}

class ProfilePageService extends FormPageService {
    constructor() {
        super('form-profile', 'profile');
    }

    async deleteProfile(deleteUrl?: string) {
        const loading = this.setLoading('delete');

        await ApplyService.deleteProfile();

        loading.off();
        if (deleteUrl) {
            window.location.href = deleteUrl;
        } else {
            window.location.reload();
        }
    }

    async saveAjax(data: any): Promise<boolean> {
        if (baseStore.state.user?.candidateId) {
            return !!(await ApplyService.updateProfile(data));
        } else {
            return !!(await ApplyService.createProfile(data));
        }
    }

    async loadJob(application: Application): Promise<Job | undefined> {
        let reqId = application['jobReqId'];
        let postfix = '';

        if (getConfigValue('idContainsLang')) {
            let locale = application['appLocale'];
            if (!locale) {
                let url = new URL(application['link']);
                let params = new URLSearchParams(url.search);
                locale = params.get('locale');
            }
            if (locale) postfix = '-' + locale;
        }

        let job = await JobService.getJob(reqId + postfix);
        if (!job) {
            job = await JobService.getPreviewJob(reqId);
        }
        return job;
    }

    _fullProfile?: BehaviorSubject<FullProfile>;

    loadFullProfile(options?: { loadJobs: boolean }): BehaviorSubject<FullProfile> {
        // Return previous if allready called
        if (this._fullProfile) return this._fullProfile;

        const reload = async () => {
            const loading = this.setLoading('load');
            await loadAndFillProfile();
            loading.off();
        };

        const signinNeeded = () => !AuthService.isLoggedIn() || AuthService.isAnonymous();
        const fullProfile: FullProfile = { signinNeeded: signinNeeded(), reload };
        const result = (this._fullProfile = new BehaviorSubject<FullProfile>(fullProfile));

        AuthService.waitForInitialUser().then(async user => {
            fullProfile.signinNeeded = signinNeeded();
            result.next({ ...fullProfile });

            const loading = this.setLoading('load');
            await this.fillEmail(user);
            await loadAndFillProfile();

            loading.off();
            // This call may take a very long time - showing a loading dialog is not advisable
            if (options?.loadJobs) await loadApplicationJobs();
        });

        async function loadAndFillProfile() {
            fullProfile.profile = await profilePageService.loadProfile();
            if (fullProfile.profile?.jobs) {
                fullProfile.applications = fullProfile.profile.jobs.map(x => ({ application: x }));
            }

            if (fullProfile.profile) {
                await profilePageService.fill(fullProfile.profile);
            }
            result.next({ ...fullProfile });
        }

        let loadApplicationJobs = async () => {
            if (fullProfile.applications) {
                let loader = fullProfile.applications.map(async container => {
                    container.job = await this.loadJob(container.application);
                    if (container.job) this._fullProfile!.next({ ...fullProfile });
                });
                await Promise.all(loader);
            }
        };

        return result;
    }

    async retractApplication(applicationId: string) {
        const loading = this.setLoading('retract', 'application');
        const result = await ApplyService.retractApplication(applicationId);

        if (result) {
            const profile = await profilePageService.loadProfile();

            if (this._fullProfile) {
                let state = this._fullProfile.value;
                state.profile = profile;

                let applications: Application[] = profile?.jobs || [];
                let hasApplication = ({ application }: { application: Application }) => {
                    return applications.find(x => x.applicationId === application.applicationId);
                };
                state.applications = state.applications?.filter(hasApplication);
                this._fullProfile.next({ ...state });
            }
        } else {
            baseStore.state.error = {}
            baseStore.state.errorId = 'retract-application';
        }
        loading.off();
    }
}

export const profilePageService = new ProfilePageService();